import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { startCase } from "lodash";
import { Download } from "lucide-react";
import { ViewColumn } from "@mui/icons-material";
import SummarizedInventoryTable from './TableComponent';
import * as XLSX from 'xlsx-js-style';
import LogisticsManagementAtom, { fetchLaydownData } from '../../../atoms/logistics/LogisticsManagementAtom';
import { useAtom } from 'jotai';
import reportsAtoms, { fetchSummarizedInventoryDataAtom } from '../../../atoms/reports/ReportsAtom';
import { getCheckedColumns, setCheckedColumns, getColumnStorageKey } from '../../../shared/columnSelection';

const LOCAL_STORAGE_KEY = getColumnStorageKey('summarized_inventory');
const defaultVisibleSummarized = [
  'part_name',
  'description',
  'currentQty',
  'damagedQty',
  'distributedQty',
  'installedQty',
  'quantityToReceive'
];

const allColumnCategories = [
  { key: 'part_name', label: 'Part Number', category: 'basic' },
  { key: 'description', label: 'Part Description', category: 'basic' },
  { key: 'currentQty', label: 'Current Quantity', category: 'quantity' },
  { key: 'damagedQty', label: 'Damaged Quantity', category: 'quantity' },
  { key: 'remediateQty', label: 'Remediated Quantity', category: 'quantity' },
  { key: 'distributedQty', label: 'Distributed Quantity', category: 'summary' },
  { key: 'installedQty', label: 'Installed Quantity', category: 'summary' },
  { key: 'quantityToReceive', label: 'Quantity yet to receive', category: 'summary' }
];

const exportSummarizedInventoryToExcel = (inventoryData, laydownList, projectInfo = {}) => {
  const excelData = [];

  if (projectInfo.projectNumber) {
    excelData.push(['Project Number', projectInfo.projectNumber]);
  }
  if (projectInfo.projectName) {
    excelData.push(['Project Name', projectInfo.projectName]);
  }
  if (projectInfo.reportTitle) {
    excelData.push(['Report', projectInfo.reportTitle]);
  }
  if (projectInfo.reportDate) {
    excelData.push(['Report Date', projectInfo.reportDate]);
  }

  excelData.push(['']); // Empty row

  const headers = [
    'Part Number',
    'Part Description',
    ...laydownList.flatMap(laydown => [
      `${laydown.name} - Current`,
      `${laydown.name} - Damaged`,
      `${laydown.name} - Remediated`
    ]),
    'Distributed Quantity',
    'Installed Quantity',
    'Quantity yet to receive'
  ];

  excelData.push(headers);

  inventoryData.forEach(item => {
    const row = [
      item.part_name || '',
      item.description || '',
      ...laydownList.flatMap(laydown => [
        item.laydownData?.[laydown.id]?.currentQty || 0,
        item.laydownData?.[laydown.id]?.damagedQty || 0,
        item.laydownData?.[laydown.id]?.remediateQty || 0
      ]),
      item.distributed_qty || 0,
      item.installed_qty || 0,
      (item.required_qty || 0) - (item.delivered_qty || 0)
    ];
    excelData.push(row);
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(excelData);

  const columnWidths = [
    { wch: 20 },
    { wch: 40 },
    ...laydownList.flatMap(() => [
      { wch: 15 },
      { wch: 15 },
      { wch: 15 }
    ]),
    { wch: 18 },
    { wch: 18 },
    { wch: 20 }
  ];

  ws['!cols'] = columnWidths;

  XLSX.utils.book_append_sheet(wb, ws, "Summarized Inventory");

  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  const filename = `Summarized_Inventory_Report_${dateStr}.xlsx`;

  XLSX.writeFile(wb, filename);
};

const SummarizedInventoryComponent = ({ title = "Summarized Inventory Report" }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState(() => {
    const stored = getCheckedColumns(LOCAL_STORAGE_KEY);
    if (stored.length > 0) return stored;
    return defaultVisibleSummarized;
  });
  const [loader, setLoader] = useState(false);
  const [reportsData] = useAtom(reportsAtoms);
  const [downloadLoader, setDownloadLoader] = useState(false);
  const [, setLaydown] = useAtom(fetchLaydownData);
  const [LogisticsData] = useAtom(LogisticsManagementAtom);
  const [, setSummarizedInventory] = useAtom(fetchSummarizedInventoryDataAtom);
  const { laydownList } = LogisticsData;
  const { summarizedInventoryData } = reportsData;
  const [filter, setFilter] = useState([]);

  const openFilter = Boolean(filterAnchorEl);

const fetchInitialData = useCallback(async () => {
    setLoader(true);
    try {
      if (filter?.length) {
        await Promise.all([
          setLaydown(),
          setSummarizedInventory(filter)
        ]);
      } else {
        await Promise.all([
          setLaydown(),
          setSummarizedInventory([])
        ]);
      }
    } catch (error) {
      console.error("Error fetching logistics data:", error);
    } finally {
      setLoader(false);
    }
  // eslint-disable-next-line
  }, [filter]);
  
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);
  

  const handleToggle = (key) => {
    setVisibleCols((prev) => {
      const updated = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      setCheckedColumns(LOCAL_STORAGE_KEY, updated);
      return updated;
    });
  };

  const handleDownload = async () => {
    try {
      setDownloadLoader(true);

      if (!summarizedInventoryData || summarizedInventoryData.length === 0) return;

      const projectDetails = {
        projectNumber: 'PROJ-2025-001',
        projectName: 'Sample Project',
        reportTitle: 'Summarized Inventory Report',
        reportDate: new Date().toLocaleDateString()
      };

      await new Promise(resolve => setTimeout(resolve, 300));

      exportSummarizedInventoryToExcel(summarizedInventoryData, laydownList, projectDetails);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
    } finally {
      setDownloadLoader(false);
    }
  };
 
  useEffect(() => {
    setCheckedColumns(LOCAL_STORAGE_KEY, visibleCols);
  }, [visibleCols]);
  
  return (
    <Box sx={{ position: "relative" }}>
        <Box sx={{ padding: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box>
               <Typography fontSize={22} fontWeight={600}>{startCase(title)}</Typography>
               
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                    variant="outlined"
                    size="small"
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    value={filter.length === 0 ? "Filter Laydowns" : `Filtered ${filter.length} Laydowns`}
                    InputProps={{
                        readOnly: true,
                        sx: {
                        cursor: 'pointer',
                        height: 40,
                        fontSize: 16,
                        bgcolor: filter.length ? "#f16253" : 'white',
                        color: filter.length ? "white" : 'black',
                        '& .MuiInputBase-input': {
                            color: filter.length ? "white" : 'black',
                        }
                        }
                    }}
                    sx={{
                        width: 200,
                        '& .MuiOutlinedInput-root': {
                        bgcolor: filter.length ? "#f16253" : 'white',
                        '& fieldset': {
                            borderColor: filter.length ? '#f16253' : undefined,
                        },
                        '&:hover fieldset': {
                            borderColor: '#f16253',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#f16253',
                        },
                        },
                    }}
                />
              <Button
                variant="outlined"
                startIcon={downloadLoader ? <CircularProgress size={16} /> : <Download size={16} />}
                onClick={handleDownload}
                disabled={downloadLoader || !summarizedInventoryData?.length}
              >
                {downloadLoader ? 'Downloading...' : 'Download'}
              </Button>


              <Button variant="outlined" startIcon={<ViewColumn />} onClick={(e) => setAnchorEl(e.currentTarget)}>
                Columns
              </Button>
            </Box>

            {/* Column Popover */}
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box p={2} sx={{ display: 'flex', flexDirection: 'column', fontSize: "16px", minWidth: 250 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Select Columns</Typography>
                {allColumnCategories.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={<Checkbox size="small" checked={visibleCols.includes(col.key)} onChange={() => handleToggle(col.key)} />}
                    label={<Box fontSize={16}>{col.label}</Box>}
                  />
                ))}
              </Box>
            </Popover>

            {/* Filter Popover - FIXED */}
            <Popover
              open={openFilter}
              anchorEl={filterAnchorEl}
              onClose={() => setFilterAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              disableRestoreFocus={true}
              disableAutoFocus={true}
              disableEnforceFocus={true}
            >
              <Box sx={{ p: 2, width: 250, maxHeight: 400, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>Filter</Typography>
                  <Button
                    variant="text"
                    size="small"
                    sx={{ color: 'blue', textTransform: 'none' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilterAnchorEl(null);
                      setFilter([]);
                    }}
                  >
                    Clear
                  </Button>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filter.length === laydownList.length}
                      indeterminate={filter.length > 0 && filter.length < laydownList.length}
                      onChange={(e) => {
                        e.stopPropagation();
                        setFilterAnchorEl(null);
                        if (e.target.checked) setFilter(laydownList.map(item => Number(item?.id)));
                        else setFilter([]);
                      }}
                    />
                  }
                  label="Select All"
                  onClick={(e) => e.stopPropagation()}
                />

                {laydownList.map((item) => (
                  <FormControlLabel
                    key={item?.Buttonid}
                    control={
                      <Checkbox
                        checked={filter.includes(Number(item?.id))}
                        onChange={(e) => {
                          e.stopPropagation();
                          setFilterAnchorEl(null);
                          setFilter(prev =>
                            prev.includes(Number(item?.id)) ? prev.filter(f => f !== Number(item?.id)) : [...prev, Number(item?.id)]
                          );
                        }}
                      />
                    }
                    label={item?.name}
                    onClick={(e) => e.stopPropagation()}
                  />
                ))}
              </Box>
            </Popover>
          </Box>

          <SummarizedInventoryTable
            rows={summarizedInventoryData}
            visibleCols={visibleCols}
            laydownList={laydownList}
            loader={loader}
            filter={filter}
          />
        </Box>
    </Box>
  );
};

export default SummarizedInventoryComponent;